import React from 'react';
import { Wifi, UserCheck, Home, MessageCircle, CheckCircle2, Sparkles } from 'lucide-react';
import { advantages, images } from '../data/mock';

const iconMap = {
  Wifi, UserCheck, Home, MessageCircle
};

const Advantages = () => {
  return (
    <section id="keunggulan" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            Keunggulan Kami
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Kenapa Memilih <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-700">Genius?</span>
          </h2>
          <p className="text-lg text-gray-600">
            Kami berkomitmen memberikan pengalaman belajar terbaik dengan fasilitas dan layanan unggulan
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative order-2 lg:order-1">
            <div className="relative">
              <img 
                src={images.teaching}
                alt="Tutor Genius" 
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
              />
              {/* Overlay Card */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">100% Kepuasan</h4>
                    <p className="text-sm text-gray-500">Siswa kami puas dengan hasil belajar</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative Elements */}
            <div className="absolute -top-4 -left-4 w-20 h-20 bg-orange-200 rounded-2xl -z-10" />
            <div className="absolute -bottom-4 right-20 w-16 h-16 bg-blue-200 rounded-xl -z-10" />
          </div>

          {/* Advantages List */}
          <div className="space-y-6 order-1 lg:order-2">
            {advantages.map((advantage, idx) => {
              const IconComponent = iconMap[advantage.icon];
              return (
                <div
                  key={advantage.id}
                  className="group flex items-start gap-5 p-6 bg-gradient-to-r from-gray-50 to-white rounded-2xl hover:from-blue-50 hover:to-white hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/20">
                    {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {advantage.name}
                    </h3>
                    <p className="text-gray-600">{advantage.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Features Banner */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          {/* Decorative */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/20 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10 grid md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Jadwal Fleksibel</h4>
              <p className="text-blue-100">Pilih jadwal belajar sesuai waktu luang Anda</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Sertifikat Resmi</h4>
              <p className="text-blue-100">Dapatkan sertifikat kelulusan program</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Kelas Kecil</h4>
              <p className="text-blue-100">Maksimal 8 siswa per kelas untuk fokus belajar</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Advantages;
